<!--
  BulkDuplicateModal.vue -- Phase 4.4 bulk duplicate modal.

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.4 bulk duplicate modal.

  Confirms a duplicate action against a list of selected
  CampaignProducts. Shows a preview line per source: "<source name>"
  -> "<predicted new name>" where the predicted name uses
  MacOSIncrementNamer client-side.

  Server is the final authority on collision resolution -- the
  ``/api/campaign_products/bulk_duplicate/`` endpoint runs the
  equivalent Python ``next_available_product_name`` inside the
  transaction (P4-Q12). Client-side preview is a UX affordance.

  Threading: when two source products share the same name (e.g. two
  "Cap" rows selected), the preview "consumes" the first row's
  predicted name before computing the second so the user sees
  distinct (N) suffixes (cap (1), cap (2)). This mirrors the
  server-side behavior where each duplicate is flushed before the
  next probes for collisions.
-->
<template>
    <div
        v-if="modelValue"
        class="bulk-duplicate-modal"
        data-testid="bulk-duplicate-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-duplicate-modal-title"
    >
        <div class="bulk-duplicate-modal__backdrop" @click="onCancel"></div>
        <div class="bulk-duplicate-modal__panel">
            <h2
                id="bulk-duplicate-modal-title"
                class="bulk-duplicate-modal__title"
            >
                Duplicate products
            </h2>
            <p class="bulk-duplicate-modal__lede">
                {{ sourceProducts.length }}
                {{
                    sourceProducts.length === 1 ? 'product' : 'products'
                }}
                will be duplicated. New names follow macOS-style "(N)"
                numbering.
            </p>
            <ul class="bulk-duplicate-modal__preview-list">
                <li
                    v-for="entry in previewEntries"
                    :key="entry.id"
                    class="bulk-duplicate-modal__preview-row"
                    :data-testid="`bulk-duplicate-preview-${entry.id}`"
                >
                    <span class="bulk-duplicate-modal__preview-name">
                        {{ entry.sourceName }}
                    </span>
                    <span class="bulk-duplicate-modal__preview-arrow">
                        &rarr;
                    </span>
                    <span class="bulk-duplicate-modal__preview-new-name">
                        {{ entry.previewName }}
                    </span>
                </li>
            </ul>
            <div class="bulk-duplicate-modal__actions">
                <button
                    type="button"
                    class="bulk-duplicate-modal__btn"
                    data-testid="bulk-duplicate-cancel"
                    @click="onCancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="bulk-duplicate-modal__btn bulk-duplicate-modal__btn--primary"
                    data-testid="bulk-duplicate-submit"
                    @click="onSubmit"
                >
                    Duplicate
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { nextAvailableName } from '@/utils/MacOSIncrementNamer'

export default {
    name: 'BulkDuplicateModal',
    props: {
        modelValue: {
            type: Boolean,
            required: true,
            default: false,
        },
        sourceProducts: {
            // Each entry: { id: int, product_name: string }
            type: Array,
            required: true,
            default: () => [],
        },
        existingNames: {
            // Array OR Set of product_names already taken in the store.
            type: [Array, Set],
            required: false,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'submit'],
    computed: {
        previewEntries() {
            // Thread the naming probe across sources so two sources
            // with the same product_name don't both predict "(1)".
            // We re-build a Set from existingNames, then add each
            // predicted name as we go.
            const taken = new Set(
                this.existingNames instanceof Set
                    ? Array.from(this.existingNames)
                    : (this.existingNames || []),
            )
            const out = []
            for (const src of this.sourceProducts) {
                const sourceName = src.product_name || 'Untitled product'
                const previewName = nextAvailableName(sourceName, taken)
                out.push({
                    id: src.id,
                    sourceName,
                    previewName,
                })
                // Consume the predicted name so the next source's
                // probe sees it as taken.
                taken.add(previewName)
            }
            return out
        },
    },
    methods: {
        onSubmit() {
            const ids = this.sourceProducts.map((p) => p.id)
            this.$emit('submit', { campaignProductIds: ids })
        },
        onCancel() {
            this.$emit('update:modelValue', false)
        },
    },
}
</script>

<style scoped>
.bulk-duplicate-modal {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
}
.bulk-duplicate-modal__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
}
.bulk-duplicate-modal__panel {
    position: relative;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
    padding: 24px;
    width: min(520px, 90vw);
    max-height: 80vh;
    overflow-y: auto;
    z-index: 1;
}
.bulk-duplicate-modal__title {
    margin: 0 0 8px;
    font-size: 18px;
    color: #1e293b;
}
.bulk-duplicate-modal__lede {
    margin: 0 0 16px;
    font-size: 13px;
    color: #475569;
}
.bulk-duplicate-modal__preview-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
}
.bulk-duplicate-modal__preview-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 13px;
}
.bulk-duplicate-modal__preview-row:last-child {
    border-bottom: none;
}
.bulk-duplicate-modal__preview-name {
    color: #64748b;
}
.bulk-duplicate-modal__preview-arrow {
    color: #cbd5e1;
}
.bulk-duplicate-modal__preview-new-name {
    color: #1e293b;
    font-weight: 600;
}
.bulk-duplicate-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
}
.bulk-duplicate-modal__btn {
    appearance: none;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
}
.bulk-duplicate-modal__btn--primary {
    background: #6366f1;
    border-color: #6366f1;
    color: #fff;
}
.bulk-duplicate-modal__btn--primary:hover {
    background: #4f46e5;
}
</style>
